/**
 * ============================================================================
 * DISPUTE STORE - ZUSTAND
 * ============================================================================
 * 
 * Global dispute state management
 * 
 * @typedef {Object} Dispute
 * @property {string} id
 */

import { create } from 'zustand';
import { validateId, validateArray, validateObject } from './utils/validators';
import { addItemOptimized, updateItemInArray, limitArraySize, deduplicateById } from './utils/performance';
import { createSelector } from './utils/selectors';

const useDisputeStore = create((set, get) => ({
  // State
  disputes: [],
  currentDispute: null,
  disputeStats: null,
  evidence: [],
  offers: [],
  messages: [],
  loading: false,
  error: null,
  filters: {
    status: null,
    type: null,
    dateRange: null,
  },
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
  },

      // Actions
      /**
       * Set disputes with validation and size limit
       * @param {Dispute[]} disputes - Disputes array
       */
      setDisputes: (disputes) => {
        validateArray(disputes, 'Disputes');
        const validated = disputes.map((d) => {
          validateObject(d, 'Dispute');
          if (d.id) validateId(d.id, 'Dispute ID');
          return d;
        });
        const deduplicated = deduplicateById(validated);
        const limited = limitArraySize(deduplicated, 1000);
        set({ disputes: limited });
      },

      /**
       * Add dispute with validation and deduplication
       * @param {Dispute} dispute - Dispute object
       */
      addDispute: (dispute) => {
        validateObject(dispute, 'Dispute');
        if (!dispute.id) {
          throw new Error('Dispute must have an id');
        }
        set((state) => ({
          disputes: addItemOptimized(state.disputes, dispute, {
            idKey: 'id',
            maxSize: 1000,
            prepend: true,
          }),
        }));
      },

      /**
       * Update dispute with validation
       * @param {string} disputeId - Dispute ID
       * @param {Object} updates - Updates to apply
       */
      updateDispute: (disputeId, updates) => {
        validateId(disputeId, 'Dispute ID');
        validateObject(updates, 'Updates');
        set((state) => ({
          disputes: updateItemInArray(state.disputes, disputeId, updates),
          currentDispute:
            state.currentDispute?.id === disputeId
              ? { ...state.currentDispute, ...updates }
              : state.currentDispute,
        }));
      },

  removeDispute: (disputeId) => {
    set((state) => ({
      disputes: state.disputes.filter((d) => d.id !== disputeId),
      currentDispute:
        state.currentDispute?.id === disputeId
          ? null
          : state.currentDispute,
    }));
  },

  setCurrentDispute: (dispute) => {
    set({ currentDispute: dispute });
  },

  setDisputeStats: (stats) => {
    set({ disputeStats: stats });
  },

  setEvidence: (evidence) => {
    set({ evidence });
  },

      /**
       * Add evidence with validation
       * @param {Object} evidenceItem - Evidence item
       */
      addEvidence: (evidenceItem) => {
        validateObject(evidenceItem, 'Evidence item');
        set((state) => ({
          evidence: addItemOptimized(state.evidence, evidenceItem, {
            idKey: 'id',
            maxSize: 100,
            prepend: false,
          }),
        }));
      },

  removeEvidence: (evidenceId) => {
    set((state) => ({
      evidence: state.evidence.filter((e) => e.id !== evidenceId),
    }));
  },

  setOffers: (offers) => {
    set({ offers });
  },

  addOffer: (offer) => {
    set((state) => ({
      offers: [...state.offers, offer],
    }));
  },

  updateOffer: (offerId, updates) => {
    set((state) => ({
      offers: state.offers.map((o) =>
        o.id === offerId ? { ...o, ...updates } : o
      ),
    }));
  },

  setMessages: (messages) => {
    set({ messages });
  },

  addMessage: (message) => {
    set((state) => ({
      messages: [...state.messages, message],
    }));
  },

  setFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
      pagination: { ...state.pagination, page: 1 },
    }));
  },

  clearFilters: () => {
    set({
      filters: {
        status: null,
        type: null,
        dateRange: null,
      },
      pagination: { ...get().pagination, page: 1 },
    });
  },

  setPagination: (pagination) => {
    set((state) => ({
      pagination: { ...state.pagination, ...pagination },
    }));
  },

  setLoading: (loading) => {
    set({ loading });
  },

  setError: (error) => {
    set({ error });
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set({
      disputes: [],
      currentDispute: null,
      disputeStats: null,
      evidence: [],
      offers: [],
      messages: [],
      loading: false,
      error: null,
      filters: {
        status: null,
        type: null,
        dateRange: null,
      },
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0,
      },
    });
  },
}));

// Memoized Selectors
export const selectDisputesByStatus = createSelector(
  (state, status) => {
    return state.disputes.filter((d) => d.status === status);
  }
);

export const selectDisputeStats = createSelector(
  (state) => {
    const { disputes } = state;
    return {
      total: disputes.length,
      byStatus: disputes.reduce((acc, d) => {
        acc[d.status] = (acc[d.status] || 0) + 1;
        return acc;
      }, {}),
    };
  }
);

export default useDisputeStore;
